import { Flag, Check, Clock, ArrowRight } from "lucide-react";
import { formatDate } from "~/helpers/date";
import { cn } from "~/lib/utils";
import { type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WorkflowStepStatuses, type WorkflowStepStatus } from "~/api/types";

interface TimelineIndicatorProps {
  icon: LucideIcon;
  text?: string;
  variant?: "primary" | "gray";
}

export function TimelineIndicator({
  icon: Icon,
  text,
  variant = "primary",
}: TimelineIndicatorProps) {
  return (
    <div className="ml-1.5 flex items-center">
      <div
        className={cn(
          "z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 bg-white shadow-md",
          variant === "primary" ? "border-primary/40" : "border-border/40",
        )}
      >
        <Icon
          className={cn(
            "h-4 w-4",
            variant === "primary" ? "text-primary" : "text-muted-foreground",
          )}
        />
      </div>
      {text && <div className="text-muted-foreground ml-4 text-sm">{text}</div>}
    </div>
  );
}

export function TimelineStart({
  startedAt,
  scheduleReviewFrom,
}: {
  startedAt?: string | null;
  scheduleReviewFrom?: string | null;
}) {
  const { t } = useTranslation();

  let text = "";
  if (startedAt) {
    text = `${t(($) => $.commons.workflow.startedAt)} ${formatDate(startedAt, "date")}`;
  } else if (scheduleReviewFrom) {
    text = `${t(($) => $.commons.document.startScheduledReview)} ${formatDate(scheduleReviewFrom, "date")}`;
  }

  return <TimelineIndicator icon={Flag} text={text || undefined} />;
}

type StepIndicatorProps = {
  value?: string;
  status: WorkflowStepStatus;
};

export function StepIndicator({ value, status }: StepIndicatorProps) {
  const StatusIcon = {
    [WorkflowStepStatuses.completed]: Check,
    [WorkflowStepStatuses.active]: ArrowRight,
    [WorkflowStepStatuses.upcoming]: Clock,
  }[status];

  return (
    <div
      className={cn(
        "z-10 ml-[9px] flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-medium shadow-md",
        {
          "bg-emerald-700 text-white":
            status === WorkflowStepStatuses.completed,
          "bg-blue-700 text-white": status === WorkflowStepStatuses.active,
          "border-border/50 bg-secondary text-muted-foreground border shadow-xs":
            status === WorkflowStepStatuses.upcoming,
        },
      )}
    >
      {value ?? <StatusIcon className="h-4 w-4" />}
    </div>
  );
}
